import { TOOL_TYPES } from '../store/useToolStore';
import { getLongTailSlugEntries } from './longTailSeoPages';

/**
 * Single source of truth for:
 * - legacyPath: current /tools/* routes (kept working, redirected)
 * - seoPath: new root slugs (canonical)
 * - toolType: zustand TOOL_TYPES
 * - seoKey: key in SEO_DATA
 *
 * NOTE: seoKey is also used to drive on-page content blocks.
 */
export const BASE_TOOL_SLUGS = [
  {
    seoKey: 'gradient',
    toolType: TOOL_TYPES.GRADIENT,
    legacyPath: '/tools/gradient',
    seoPath: '/swiftui-gradient-generator',
    isCanonical: true,
  },
  {
    seoKey: 'loader',
    toolType: TOOL_TYPES.LOADER,
    legacyPath: '/tools/loader',
    seoPath: '/swiftui-loader-generator',
    isCanonical: true,
  },
  {
    seoKey: 'card',
    toolType: TOOL_TYPES.CARD,
    legacyPath: '/tools/card',
    seoPath: '/swiftui-card-generator',
    isCanonical: true,
  },
  {
    seoKey: 'animation',
    toolType: TOOL_TYPES.ANIMATION,
    legacyPath: '/tools/animation',
    seoPath: '/swiftui-animation-generator',
    isCanonical: true,
  },
  {
    seoKey: 'alert',
    toolType: TOOL_TYPES.ALERT,
    legacyPath: '/tools/alert',
    seoPath: '/swiftui-alert-generator',
    isCanonical: true,
  },
  {
    seoKey: 'photoPicker',
    toolType: TOOL_TYPES.PHOTO_PICKER,
    legacyPath: '/tools/photo-picker',
    seoPath: '/swiftui-photo-picker-generator',
    isCanonical: true,
  },
  {
    seoKey: 'jsonToModel',
    toolType: TOOL_TYPES.JSON_TO_MODEL,
    legacyPath: '/tools/json-to-model',
    seoPath: '/json-to-swift-codable',
    isCanonical: true,
  },
  {
    seoKey: 'apiGenerator',
    toolType: TOOL_TYPES.API_GENERATOR,
    legacyPath: '/tools/api-generator',
    seoPath: '/swift-api-client-generator',
    isCanonical: true,
  },
  {
    seoKey: 'postmanImporter',
    toolType: TOOL_TYPES.POSTMAN_IMPORTER,
    legacyPath: '/tools/postman-importer',
    seoPath: '/postman-to-code-generator',
    isCanonical: true,
  },
  {
    seoKey: 'storyboardConverter',
    toolType: TOOL_TYPES.STORYBOARD_CONVERTER,
    legacyPath: '/tools/storyboard-converter',
    seoPath: '/uikit-to-swiftui-converter',
    isCanonical: true,
  },
  {
    seoKey: 'shadow',
    toolType: TOOL_TYPES.SHADOW,
    legacyPath: '/tools/shadow',
    seoPath: '/swiftui-shadow-generator',
    isCanonical: true,
  },
  {
    seoKey: 'button',
    toolType: TOOL_TYPES.BUTTON,
    legacyPath: '/tools/button',
    seoPath: '/swiftui-button-builder',
    isCanonical: true,
  },
  {
    seoKey: 'grid',
    toolType: TOOL_TYPES.GRID,
    legacyPath: '/tools/grid',
    seoPath: '/swiftui-grid-generator',
    isCanonical: true,
  },
  {
    seoKey: 'palette',
    toolType: TOOL_TYPES.PALETTE,
    legacyPath: '/tools/color-palette',
    seoPath: '/swiftui-color-palette-generator',
    isCanonical: true,
  },
  {
    seoKey: 'jsonFormatter',
    toolType: TOOL_TYPES.JSON_FORMATTER,
    legacyPath: '/tools/json-formatter',
    seoPath: '/json-formatter',
    isCanonical: true,
  },
  {
    seoKey: 'base64',
    toolType: TOOL_TYPES.BASE64,
    legacyPath: '/tools/base64',
    seoPath: '/base64-encoder-decoder',
    isCanonical: true,
  },
];

export const TOOL_SLUGS = [...BASE_TOOL_SLUGS, ...getLongTailSlugEntries()];

const normalizePath = (path) => (path || '').split('?')[0].split('#')[0];

export const getToolSlugByPath = (path) => {
  const p = normalizePath(path);
  return TOOL_SLUGS.find((t) => t.seoPath === p || t.legacyPath === p) || null;
};

export const getSeoPathForLegacyPath = (legacyPath) => {
  const match = TOOL_SLUGS.find((t) => t.legacyPath === normalizePath(legacyPath));
  return match?.seoPath || null;
};

export const getLegacyPathForSeoPath = (seoPath) => {
  const match = TOOL_SLUGS.find((t) => t.seoPath === normalizePath(seoPath));
  return match?.legacyPath || null;
};

/** Slugs shown in header/footer nav (excludes long-tail variants and hidden tools). */
export const getNavToolSlugs = () =>
  TOOL_SLUGS.filter(
    (t) => t.showInNav !== false && t.toolType !== TOOL_TYPES.STORYBOARD_CONVERTER
  );

/** Canonical slug for a tool type (hub URL for home cards and toolbar). */
export const getCanonicalSlugForToolType = (toolType) =>
  TOOL_SLUGS.find((t) => t.toolType === toolType && t.isCanonical) ||
  TOOL_SLUGS.find((t) => t.toolType === toolType);

export const getCanonicalSeoPathForToolType = (toolType) =>
  getCanonicalSlugForToolType(toolType)?.seoPath || '/';
